import {
    Box,
    Group,
    Modal,
    type ScrollAreaProps,
    Stack,
    Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { CSSProperties, ReactNode } from 'react';
import { OpenModal } from '../../../utils/modals/openModal';
import { assertUnreachable } from '../../../utils/utilFunctions';
import { Show } from '../../control-flow/Show';

export enum DialogSize {
    SMALL = 'SMALL',
    SMALL_MEDIUM = 'SMALL_MEDIUM',
    MEDIUM = 'MEDIUM',
    MEDIUM_LARGE = 'MEDIUM_LARGE',
    LARGE = 'LARGE',
    WIDE = 'WIDE',
}

export const ResizableDialog = ({
    onClose,
    canClose,
    title,
    titleBar,
    disableBackdropClick,
    children,
    actions,
    dialogSize,
    actionsHeight = 40,
    fullScreen = false,
}: {
    title: string;
    titleBar?: ReactNode;
    onClose: () => void;
    canClose: boolean;
    disableBackdropClick: boolean;
    children: ReactNode | Array<ReactNode>;
    actions?: ReactNode | Array<ReactNode>;
    dialogSize: DialogSize;
    style?: CSSProperties; // TODO: use or remove
    actionsHeight?: number;
    fullScreen?: boolean;
    offsetScrollbars?: ScrollAreaProps['offsetScrollbars']; // TODO remove
}) => {
    const smallDialogSize = { height: 200, width: 400 };
    const smallMediumDialogSize = { height: 300, width: 600 };
    const mediumDialogSize = { height: 400, width: 800 };
    const mediumLargeDialogSize = { height: 600, width: 1200 };
    const largeDialogSize = { height: 800, width: 1600 };
    const wideDialogSize = { height: 100, width: 600 };

    const getDialogSize = (minusHeight?: number) => {
        switch (dialogSize) {
            case DialogSize.SMALL: {
                return {
                    width: smallDialogSize.width,
                    height: smallDialogSize.height - (minusHeight ?? 0),
                };
            }
            case DialogSize.SMALL_MEDIUM: {
                return {
                    width: smallMediumDialogSize.width,
                    height: smallMediumDialogSize.height - (minusHeight ?? 0),
                };
            }
            case DialogSize.MEDIUM: {
                return {
                    width: mediumDialogSize.width,
                    height: mediumDialogSize.height - (minusHeight ?? 0),
                };
            }
            case DialogSize.MEDIUM_LARGE: {
                return {
                    width: mediumLargeDialogSize.width,
                    height: mediumLargeDialogSize.height - (minusHeight ?? 0),
                };
            }
            case DialogSize.LARGE: {
                return {
                    width: largeDialogSize.width,
                    height: largeDialogSize.height - (minusHeight ?? 0),
                };
            }
            case DialogSize.WIDE: {
                return {
                    width: wideDialogSize.width,
                    height: wideDialogSize.height - (minusHeight ?? 0),
                };
            }
            default: {
                return assertUnreachable(dialogSize);
            }
        }
    };

    const isSmallViewport = useMediaQuery(
        `(max-width: ${getDialogSize().width + 100}px)`
    );
    const isFullScreen = fullScreen || isSmallViewport;

    return (
        <Modal.Root
            opened={true}
            onClose={() => {
                if (!canClose) {
                    OpenModal.UnsavedChangesModal(onClose);
                } else {
                    onClose();
                }
            }}
            closeOnClickOutside={!disableBackdropClick}
            //withinPortal={false}
            fullScreen={isFullScreen}
            size={'auto'}
            //shadow={'xl'}
        >
            <Modal.Overlay backgroundOpacity={0} />
            <Modal.Content
                style={{
                    border: '2px solid var(--mantine-primary-color-light)',
                    overflow: 'hidden',
                }}
                miw={!isFullScreen ? getDialogSize().width : undefined}
                maw={!isFullScreen ? getDialogSize().width : undefined}
                mih={!isFullScreen ? getDialogSize().height : '100vh'}
                mah={!isFullScreen ? getDialogSize().height : undefined}
            >
                <Modal.Header>
                    <Modal.Title style={{ flexGrow: 1 }}>
                        {titleBar ?? (
                            <Text c={'var(--mantine-text-secondary-color)'}>
                                {title}
                            </Text>
                        )}
                    </Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body
                    mih={!isFullScreen ? getDialogSize(60).height : undefined}
                    h={
                        !isFullScreen ?
                            getDialogSize(60).height
                        :   'calc(100vh - 60px)'
                    }
                    miw={!isFullScreen ? getDialogSize().width : undefined}
                    maw={!isFullScreen ? getDialogSize().width : undefined}
                >
                    <Stack style={{ height: '100%', overflow: 'hidden' }}>
                        <Box
                            style={{
                                flexGrow: 1,
                                maxHeight:
                                    actions ?
                                        `calc(100% - ${actionsHeight}px)`
                                    :   '100%',
                            }}
                        >
                            <Show
                                when={actions}
                                fallback={children}
                            >
                                <Box
                                    h={'100%'}
                                    style={{
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                    }}
                                >
                                    {children}
                                </Box>
                            </Show>
                        </Box>
                        <Show when={actions}>
                            <Group
                                pt={4}
                                justify="flex-end"
                            >
                                {actions}
                            </Group>
                        </Show>
                    </Stack>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
